import {
  type LoadableState,
  useEffectWithController
} from '@carvana/merch-react-hooks';
import useDeepEqualMemo from '@carvana/merch-react-hooks/useDeepEqualMemo';
import React from 'react';
import type { CustomizeRequest, CustomizeResponse } from '~/contracts/new-cars';
import fetchCustomizeOptions from '~/services/new-cars/fetchCustomizeOptions';
import { generateErrorLogger } from '~/utilities/loggers';
import pickRequestProps from '~/utilities/new-cars/pickRequestProps';

export class NoMatchingVehiclesError extends Error {
  constructor() {
    super('No matching vehicles found');
  }
}

type OwnArgs = {
  loadableState: LoadableState<CustomizeResponse>;
  request: CustomizeRequest | null;
  suppressNextRef?: React.MutableRefObject<boolean>;
};

export default function useGetCustomizeOptions({
  loadableState,
  request,
  suppressNextRef
}: OwnArgs) {
  const { setData, setError, setLoading } = loadableState;

  const requestToUse = useDeepEqualMemo(
    request ? pickRequestProps(request) : null
  );

  useEffectWithController(
    controller => {
      if (!requestToUse) return;

      if (suppressNextRef?.current) {
        suppressNextRef.current = false;

        return;
      }

      setLoading();

      fetchCustomizeOptions(requestToUse, controller)
        .then(data => ({
          ...data,
          driveTypes: [...data.driveTypes].sort(
            (a, b) => a.priceAdd - b.priceAdd
          ),
          engines: [...(data.engines ?? [])].sort(
            (a, b) => a.priceAdd - b.priceAdd
          ),
          exteriorColors: [...data.exteriorColors].sort((a, b) =>
            a.name.localeCompare(b.name)
          ),
          interiorColors: [...data.interiorColors].sort((a, b) =>
            a.name.localeCompare(b.name)
          ),
          packageOptions: [...data.packageOptions].sort((a, b) => {
            if (a.code === 'NONE') return -1;
            if (b.code === 'NONE') return 1;

            return b.priceAdd - a.priceAdd;
          })
        }))
        .then(setData)
        .catch(err => {
          const bodyText: string = err?.response?.data ?? err?.message ?? '';

          if (bodyText.includes('No matching vehicles found')) {
            setError(new NoMatchingVehiclesError());

            return;
          }

          generateErrorLogger(
            'Get Customize Options',
            { request: requestToUse },
            { callback: setError, controller }
          )(err);
        });
    },
    [requestToUse, setData, setError, setLoading, suppressNextRef]
  );
}
